import React from "react";
import { CalendarDaysIcon } from "@heroicons/react/24/outline";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
} from "@tracify/ui/select";
import { useGranularity } from "@lib/hooks/use-granularity";
import type { GranularityPreference } from "@lib/util-functions/granularity";

const GRANULARITY_LABELS: Record<GranularityPreference, string> = {
  daily: "Daily",
  weekly: "Weekly",
  monthly: "Monthly",
  none: "None",
};

interface GranularityDropdownProps {
  /** Optional: for per-dashboard override (not using global atom) */
  value?: GranularityPreference;
  onChange?: (value: GranularityPreference) => void;
}

const GranularityDropdown: React.FC<GranularityDropdownProps> = ({
  value,
  onChange,
}) => {
  const {
    preference: globalPreference,
    allOptionsWithStatus,
    setPreference: setGlobalPreference,
  } = useGranularity();

  // Use provided value/onChange or fall back to global atom
  const preference = value ?? globalPreference;
  const setPreference = onChange ?? setGlobalPreference;

  const handleChange = (newValue: string) => {
    setPreference(newValue as GranularityPreference);
  };

  return (
    <Select value={preference} onValueChange={handleChange}>
      <SelectTrigger id="granularity-select-trigger">
        <span className="truncate font-semibold mr-2 inline-flex items-center">
          <CalendarDaysIcon className="h-4 w-4 inline-block mr-2" />
          <span>{GRANULARITY_LABELS[preference]}</span>
        </span>
      </SelectTrigger>
      <SelectContent className="relative max-w-[270px]">
        <SelectGroup className="space-y-1">
          <SelectLabel>Time Breakdown</SelectLabel>
          {allOptionsWithStatus.map((option) => (
            <SelectItem
              key={option.value}
              value={option.value}
              disabled={!option.enabled}
              data-testid={`granularity-select-item-${option.value}`}
            >
              <div className="text-sm font-medium leading-tight">
                {option.label}
              </div>
              {!option.enabled && option.disabledReason && (
                <div className="mt-1 text-xs font-normal leading-none text-muted-foreground">
                  {option.disabledReason}
                </div>
              )}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
};

export default GranularityDropdown;
